import { ApiClient } from "./client";
import { supabase } from "@/integrations/supabase/client";
import { sortPlaylistsByPriority, FEATURED_PLAYLIST_IDS } from "@/utils/dataUtils/prioritySort";

class PlaylistsApi extends ApiClient {
  /**
   * Fetch all playlists
   */
  async getAllPlaylists() {
    console.log("getAllPlaylists called - fetching all playlists");
    
    const result = await this.executeQuery(
      async () => await supabase
        .from('playlists')
        .select('*, channels(name)')
        .order('name'),
      "Failed to fetch playlists"
    );
    
    if (!result) {
      console.log("No result returned from query");
      return { data: [], error: null };
    }
    
    if ('error' in result && result.error) {
      console.error("Error in result:", result.error);
      return { data: [], error: null };
    }
    
    if (!('data' in result) || !result.data) {
      console.log("No data in result");
      return { data: [], error: null };
    }
    
    const playlistData = Array.isArray(result.data) ? result.data : [];
    const sortedData = sortPlaylistsByPriority(playlistData);
    
    return { data: sortedData, error: null };
  }

  /**
   * Fetch playlists by channel ID
   */
  async getPlaylistsByChannelId(channelId: string) {
    console.log(`getPlaylistsByChannelId called with channelId: ${channelId}`);
    
    const result = await this.executeQuery(
      async () => await supabase
        .from('playlists')
        .select('*')
        .eq('channel_id', channelId),
      `Failed to fetch playlists for channel ${channelId}`
    );
    
    if (!result) {
      console.log("No result returned from query");
      return { data: [], error: null };
    }
    
    // Check if there's an error field on the result object
    if ('error' in result && result.error) {
      console.error("Error in result:", result.error);
      return { data: [], error: result.error };
    }
    
    // Check if there's data field on the result object and ensure it's an array
    if (!('data' in result) || !result.data) {
      console.log("No data in result");
      return { data: [], error: null };
    }
    
    const playlistData = Array.isArray(result.data) ? result.data : [];
    const sortedData = sortPlaylistsByPriority(playlistData);
    
    return { data: sortedData, error: null };
  }

  /**
   * Fetch a playlist by ID
   */
  async getPlaylistById(id: string) {
    return this.executeQuery(
      async () => await supabase
        .from('playlists')
        .select('*, channels(id, name, logo)')
        .eq('id', id)
        .maybeSingle(),
      `Failed to fetch playlist with ID ${id}`
    );
  }

  /**
   * Increment playlist view count
   */
  async incrementPlaylistView(playlistId: string) {
    return this.executeQuery(
      async () => await supabase.rpc('increment_playlist_views_secure', { playlist_id: playlistId }),
      `Failed to increment view count for playlist ${playlistId}`
    );
  }
}

export const playlistsApi = new PlaylistsApi();
